// components/sidebar/Sidebar.tsx
import React, { useState, useMemo } from "react";
import "../../styles/components/Sidebar.css";
import { NodeConfig } from "../../../../../core/data/redux/automations/types";

interface SidebarProps {
  workflow: any;
  nodeConfigs: any;
}

const Sidebar: React.FC<SidebarProps> = ({ workflow, nodeConfigs }) => {
  const [activeTab, setActiveTab] = useState<
    "triggers" | "conditions" | "actions"
  >("triggers");
  const [searchTerm, setSearchTerm] = useState("");

  // Organize nodes by category with fallback to empty arrays
  const categorizedNodes = useMemo(() => {
    return {
      triggers: nodeConfigs?.triggers || [],
      conditions: nodeConfigs?.conditions || [],
      actions: nodeConfigs?.actions || [],
    };
  }, [nodeConfigs]);

  // Filter nodes based on search
  const filteredNodes = useMemo(() => {
    const nodes = categorizedNodes[activeTab] || [];
    if (!searchTerm) return nodes;

    return nodes.filter(
      (node: any) =>
        node.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categorizedNodes, activeTab, searchTerm]);

  const onDragStart = (event: React.DragEvent, nodeConfig: NodeConfig) => {
    event.dataTransfer.effectAllowed = "move";

    // Map category to singular form for node type
    const categoryToType: Record<string, string> = {
      triggers: "trigger",
      conditions: "condition",
      actions: "action",
    };

    const nodeType = categoryToType[nodeConfig.category] || nodeConfig.category;

    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.setData(
      "nodeData",
      JSON.stringify({
        type: nodeType,
        label: nodeConfig.name,
        category: nodeType,
        nodeKey: nodeConfig.key,
        icon: nodeConfig.icon,
        color: nodeConfig.color,
        config: {},
        fields: nodeConfig.fields,
      })
    );
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "triggers":
        return "⚡";
      case "conditions":
        return "🔍";
      case "actions":
        return "⚙️";
      default:
        return "📦";
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "triggers":
        return "#3b82f6";
      case "conditions":
        return "#f59e0b";
      case "actions":
        return "#10b981";
      default:
        return "#6b7280";
    }
  };

  return (
    <div className="workflow-sidebar">
      {/* Header */}
      <div className="workflow-sidebar-header">
        <h1>Workflow Nodes</h1>
        <p>Drag and drop to canvas</p>
      </div>

      {/* Tabs */}
      <div className="workflow-sidebar-tabs">
        {(["triggers", "conditions", "actions"] as const).map((tab) => (
          <button
            key={tab}
            className={`workflow-sidebar-tab ${
              activeTab === tab ? "active" : ""
            }`}
            onClick={() => setActiveTab(tab)}>
            <span className="tab-icon">{getCategoryIcon(tab)}</span>
            <span className="tab-label">
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </span>
            <span className="tab-count">
              ({categorizedNodes[tab]?.length || 0})
            </span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="workflow-sidebar-content">
        {filteredNodes && filteredNodes.length > 0 ? (
          <>
            {filteredNodes.map((nodeConfig: any) => (
              <div
                key={nodeConfig.key}
                className={`node-item node-item-${activeTab.slice(0, -1)}`}
                draggable
                onDragStart={(e) => onDragStart(e, nodeConfig)}>
                <div className="node-item-border"></div>
                <div className="node-item-content">
                  {/* Icon and Title Row */}
                  <div className="sidebar-node-header">
                    <div
                      className="node-left"
                      style={{
                        background: `${getCategoryColor(activeTab)}15`,
                        color: getCategoryColor(activeTab),
                      }}>
                      {nodeConfig.icon}
                    </div>
                    <div className="node-right">
                      <div className="node-name">{nodeConfig.name}</div>
                      <div className="node-desc">{nodeConfig.description}</div>
                    </div>
                  </div>

                  {/* Description Row */}
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="empty-state">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <p>No {activeTab} found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
